import { useState, useEffect, useRef } from 'react'
import { motion } from 'framer-motion'
import { useSectionsStore } from './store'

// Import logo SVGs
import token2049Logo from '/Logo/token 2049.svg'
import artBaselLogo from '/Logo/art basel.svg'
import nftNycLogo from '/Logo/NFT nyc.svg'
import nfcSummitLogo from '/Logo/NFC SUMMIT.svg'

// TypewriterText component for text animation effects
const TypewriterText = ({ text, visible, delay = 0, duration = 1, useAsciiEffect = false, customStyle = {}, segments = null, className = "" }) => {
  const [displayedText, setDisplayedText] = useState("");
  const [isAnimating, setIsAnimating] = useState(false);
  
  // Reduced character set for cleaner scrambling
  const randomChars = 'ABCDEFGHIJKLMOPRSTUWXYZ1456789_#㊥ΨﭛΦ♬҂彡益言㋛ツ۝ꈊ⽘✪ⓧ★ಢ艸웃❣';
  
  useEffect(() => {
    if (!visible) {
      setDisplayedText("");
      setIsAnimating(false);
      return;
    }
    
    let animationFrame;
    let startTime;
    
    // Start animation after delay
    const startTimer = setTimeout(() => {
      setIsAnimating(true);
      startTime = Date.now();
      
      // Create scramble plan for each character (3-5 scrambles each)
      const scramblePlan = [];
      for (let i = 0; i < text.length; i++) {
        if (text[i] === ' ') {
          scramblePlan.push({ scrambles: [], finalChar: ' ' });
        } else {
          const numScrambles = Math.floor(Math.random() * 3) + 3; // Randomly 3-5 scrambles (increased from 2-3)
          const scrambles = [];
          for (let j = 0; j < numScrambles; j++) {
            scrambles.push(randomChars[Math.floor(Math.random() * randomChars.length)]);
          }
          scramblePlan.push({ scrambles, finalChar: text[i] });
        }
      }
      
      const animate = () => {
        const elapsed = Date.now() - startTime;
        const totalDuration = 1800; // Slightly longer to accommodate more scrambles (increased from 1200)
        const progress = Math.min(elapsed / totalDuration, 1);
        
        if (progress >= 1) {
          // Animation complete - show final text
          setDisplayedText(text);
          setIsAnimating(false);
          return;
        }
        
        let currentText = '';
        
        for (let i = 0; i < text.length; i++) {
          const charProgress = Math.max(0, (progress * text.length - i) / 1.5); // Slower progression for more scrambling
          const plan = scramblePlan[i];
          
          if (plan.finalChar === ' ') {
            currentText += ' ';
          } else if (charProgress >= 1) {
            // Character is fully revealed
            currentText += plan.finalChar;
          } else if (charProgress > 0) {
            // Character is in scrambling phase - more dynamic scrambling
            const scrambleProgress = charProgress * plan.scrambles.length;
            const scrambleIndex = Math.floor(scrambleProgress);
            
            // Add some randomness to make more letters change
            const randomFactor = Math.sin(elapsed * 0.01 + i * 0.3) * 0.3; // Sine wave for smooth variation
            const adjustedProgress = Math.max(0, scrambleProgress + randomFactor);
            const finalIndex = Math.floor(adjustedProgress) % plan.scrambles.length;
            
            if (finalIndex < plan.scrambles.length) {
              // Show current scramble with some probability of showing real character
              const showRealChance = charProgress * 0.6; // Lower chance to show real character
              if (Math.random() < showRealChance) {
                currentText += plan.finalChar;
              } else {
                currentText += plan.scrambles[finalIndex];
              }
            } else {
              // Show final character
              currentText += plan.finalChar;
            }
          } else {
            // Character hasn't started yet - show first scramble with some variation
            const initialVariation = Math.floor((elapsed * 0.005 + i * 0.2) % plan.scrambles.length);
            currentText += plan.scrambles[initialVariation] || plan.finalChar;
          }
        }
        
        setDisplayedText(currentText);
        animationFrame = requestAnimationFrame(animate);
      };
      
      animate();
    }, delay * 500);
    
    return () => {
      clearTimeout(startTimer);
      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
      }
    };
  }, [text, visible, delay]);
  
  // Function to render text with styled segments
  const renderTextWithStyles = () => {
    if (!segments || segments.length === 0) {
      return displayedText;
    }

    let currentPos = 0;
    let result = [];
    let segmentIndex = 0;
    let currentSegmentText = segments[0].text;
    let currentSegmentColor = segments[0].color;
    let segmentStart = 0;

    for (let i = 0; i < displayedText.length; i++) {
      if (segmentIndex < segments.length && i >= segmentStart + currentSegmentText.length) {
        segmentIndex++;
        segmentStart = i;
        if (segmentIndex < segments.length) {
          currentSegmentText = segments[segmentIndex].text;
          currentSegmentColor = segments[segmentIndex].color;
        }
      }

      if (i === segmentStart) {
        if (currentPos < i) {
          result.push(<span key={currentPos}>{displayedText.slice(currentPos, i)}</span>);
        }
        currentPos = i;
      }

      if (segmentIndex < segments.length && i === segmentStart + currentSegmentText.length - 1) {
        result.push(
          <span key={currentPos} style={{ color: currentSegmentColor }}>
            {displayedText.slice(currentPos, i + 1)}
          </span>
        );
        currentPos = i + 1;
      }
    }

    if (currentPos < displayedText.length) {
      result.push(<span key={currentPos}>{displayedText.slice(currentPos)}</span>);
    }

    return result;
  };

  return (
    <motion.p
      className={className}
      style={{ 
        lineHeight: '1.8',
        marginBottom: '1rem',
        position: 'relative',
        fontFamily: 'inherit',
        textTransform: 'uppercase',
        color: 'white',
        ...customStyle // Merge custom styles passed via prop
      }}
    >
      {segments ? renderTextWithStyles() : displayedText}
      {isAnimating && (
        <motion.span
          animate={{ opacity: [1, 0] }}
          transition={{ repeat: Infinity, duration: 0.7 }}
          style={{ 
            display: 'inline-block',
            width: '2px',
            height: '1.4rem',
            backgroundColor: '#64ffda',
            marginLeft: '2px',
            verticalAlign: 'middle'
          }}
        />
      )}
    </motion.p>
  );
};

export const Studio = () => {
  const { horizontalProgress } = useSectionsStore();
  const panelRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [showLogos, setShowLogos] = useState(false);

  useEffect(() => {
    if (!panelRef.current) return;

    const slideStart = 0.9; // Start sliding at 85% progress
    const slideEnd = 1.0;   // Fully visible at 100% progress

    let translateY;
    if (horizontalProgress < slideStart) {
      translateY = 100; // Off-screen bottom
    } else if (horizontalProgress >= slideEnd) {
      translateY = 0;   // Fully on-screen
    } else {
      const t = (horizontalProgress - slideStart) / (slideEnd - slideStart);
      translateY = 100 - (t * 100); // Smooth transition from 100 to 0
    }

    panelRef.current.style.transform = `translateY(${translateY}vh)`;
    // Set pointerEvents to none to prevent Studio panel from intercepting scroll events
    panelRef.current.style.pointerEvents = 'none';

    if (translateY < 50 && !isVisible) {
      setIsVisible(true);
    }
  }, [horizontalProgress]);

  // Trigger logo fade-in after text animations complete
  useEffect(() => {
    if (isVisible) {
      const timer = setTimeout(() => {
        setShowLogos(true);
      }, 500); // 2 seconds after component becomes visible
      
      return () => clearTimeout(timer);
    } else {
      setShowLogos(false);
    }
  }, [isVisible]);

  // Split the text into main paragraph and last sentence
  const mainTextSegments = [
    { text: "Glitch NFT Studio is a collective of artists, creative coders ", color: "white" },
    { text: "&", color: "#64ffda" },
    { text: " entrepreneurs with over a decade of experience at the intersection of ", color: "white" },
    { text: "art", color: "#64ffda" },
    { text: " and technology specializing in web", color: "white" },
    { text: "3", color: "#64ffda" },
    { text: " and generative ", color: "white" },
    { text: " AI", color: "#64ffda" },
    { text: ". Our projects include immersive installations, metaverse scenes, generative art", color: "white" },
    { text: "& ", color: "#5FF916" },
    { text: "  AI-driven animations.", color: "white" }
  ];
  const lastSentence = "We collaborate with high profile clients to create unique digital experiences that bridge the gap between technology and art.";

  return (
    <div
      ref={panelRef}
      className="studio-panel"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100vh',
        background: '#000',
        zIndex: 2000,
        transition: 'transform 0.1s ease-out',
      }}
    >
      <div 
        style={{ 
          padding: '5rem', 
          color: 'white',
          maxWidth: '85vw',
          margin: '0 auto',
          marginLeft: 'calc(50% - 40vw)',
          position: 'relative',
        }}
      >
        <h1 
          style={{ 
            fontSize: 'min(4vw, 5vw)',
            fontWeight: 600,
            textShadow: '0 0 30px rgba(100, 255, 218, 0.5)',
            marginBottom: '20px',
            marginTop: '4rem',
            marginLeft: '0.5vw',
            textAlign: 'center',
            color: '#64ffda',
            letterSpacing: '1px',
            lineHeight: '1'
          }}
        >
          Glitch Candies Studio
        </h1>
        
        <div>
          <TypewriterText 
            text="Glitch NFT Studio is a collective of artists, creative coders & entrepreneurs with over a decade of experience at the intersection of art and technology specializing in web3 and generative AI. Our projects include immersive installations, metaverse scenes, generative ART & AI-driven animations."
            visible={isVisible}
            delay={0.5}
            segments={mainTextSegments}
            customStyle={{
              fontWeight: 100,
              fontSize: 'min(1.8vw, 2.5vw)',
              fontFamily: 'Arial, sans-serif',
              marginTop: '5rem',
              marginLeft: '50px',
              color: 'white',
              display: 'block',
              marginBottom: '1rem'
            }}
          />
        </div>
        <TypewriterText 
          text={lastSentence}
          visible={isVisible}
          delay={1.8}
          customStyle={{
            marginLeft: '50px',
            fontSize: 'min(1.8vw, 2.5vw)',
            fontFamily: 'Arial, sans-serif',
            marginTop: '3.5rem',
            color: '#a0a0a0'
          }}
        />
        
        {/* Logo row - events where we presented or collaborated */}
        <div style={{
          display: 'flex',
          justifyContent: 'flex-start',
          alignItems: 'center',
          gap: '5rem',
          marginTop: '3rem',
          marginLeft: '10vw',
          marginBottom: '2rem',
          opacity: showLogos ? 1 : 0,
          transition: 'opacity 1.5s ease-in-out'
        }}>
          <img src={token2049Logo} alt="Token 2049" style={{ height: '7vw', filter: 'brightness(0.9)', opacity: 0.9 }} />
          <img src={artBaselLogo} alt="Art Basel" style={{ height: '7vw', filter: 'brightness(0.9)', opacity: 0.9 }} />
          <img src={nftNycLogo} alt="NFT NYC" style={{ height: '7vw', filter: 'brightness(0.9)', opacity: 0.9 }} />
          <img src={nfcSummitLogo} alt="NFC Summit" style={{ height: '7vw', filter: 'brightness(0.9)', opacity: 0.9 }} />
        </div>
      </div>
    </div>
  );
};